import type { ConfigurationContextValue } from "./ConfigurationContext";

/**
 * Defaults de Musco CRM
 *
 * Esta es la fuente de verdad de la configuración inicial — title, sectores,
 * etapas, categorías, etc. El equipo comercial puede modificar cualquiera de
 * estos valores en `/settings` y los cambios se persisten en el config store.
 *
 * Modificada por Oscar (2026-05-10) basado en investigación + decisiones de
 * dominio con Alonso para el cliente Musco Lighting México.
 */

/**
 * Logo oficial de Musco (descargado de Wikimedia Commons — licencia libre).
 *
 * El SVG original usa 2 colores de marca:
 *   - rgb(0,100,166) = #0064A6 Endeavour Blue (color primario Musco)
 *   - rgb(255,221,37) = #FFDD25 amarillo (color secundario, tiene buen contraste sobre negro)
 *
 * Para `dark mode`, el azul se reemplaza por blanco para legibilidad sobre
 * fondo oscuro. El amarillo se conserva en ambos modos.
 */
export const defaultDarkModeLogo = "./logos/musco_logo_dark.svg";
export const defaultLightModeLogo = "./logos/musco_logo_light.svg";

export const defaultCurrency = "MXN";

export const defaultTitle = "Musco CRM";

// Sectores donde Musco Lighting vende soluciones de iluminación deportiva LED.
export const defaultCompanySectors = [
  { value: "estadio-profesional", label: "Estadio profesional" },
  { value: "club-deportivo-privado", label: "Club deportivo privado" },
  { value: "universidad-escuela", label: "Universidad / escuela" },
  { value: "gobierno-municipal", label: "Gobierno / municipal" },
];

// Pipeline B2B largo para iluminación deportiva: prospecto → visita técnica
// al sitio → cotización enviada → negociación → cerrado.
//
// IMPORTANTE: los valores "won" y "lost" están hardcoded en la lógica interna
// del CRM (DealsChart.tsx, DealsPipeline.tsx). Mantenemos esos values en
// inglés y solo traducimos el label. Los demás stages son seguros de
// renombrar a español porque no están hardcoded en ningún otro lugar.
export const defaultDealStages = [
  { value: "prospecto", label: "Prospecto" },
  { value: "visitado", label: "Visitado" },
  { value: "cotizado", label: "Cotizado" },
  { value: "negociacion", label: "Negociación" },
  { value: "won", label: "Ganado" },
  { value: "lost", label: "Perdido" },
];

// "won" es el único stage que cuenta como pipeline activo (forecast de revenue).
export const defaultDealPipelineStatuses = ["won"];

// Líneas de producto Musco. Modificables en Settings → Categorías si Max
// confirma nombres diferentes durante la primera junta.
export const defaultDealCategories = [
  { value: "led-estadio", label: "LED estadio (TLC-LED)" },
  { value: "led-cancha", label: "LED cancha deportiva" },
  { value: "control-link", label: "Control-Link (IoT)" },
  { value: "mantenimiento", label: "Mantenimiento + garantía" },
  { value: "otro", label: "Otro" },
];

// Estados de "temperatura" de un contacto. Compatibles con el dashboard de Hot Contacts.
export const defaultNoteStatuses = [
  { value: "cold", label: "Frío", color: "#7dbde8" },
  { value: "warm", label: "Tibio", color: "#e8cb7d" },
  { value: "hot", label: "Caliente", color: "#e88b7d" },
  { value: "in-contract", label: "En contrato", color: "#a4e87d" },
];

// Tipos de actividad comercial típicos en B2B.
export const defaultTaskTypes = [
  { value: "none", label: "Ninguno" },
  { value: "email", label: "Correo" },
  { value: "demo", label: "Demo" },
  { value: "lunch", label: "Comida" },
  { value: "meeting", label: "Reunión" },
  { value: "follow-up", label: "Seguimiento" },
  { value: "thank-you", label: "Agradecimiento" },
  { value: "ship", label: "Envío" },
  { value: "call", label: "Llamada" },
];

export const defaultConfiguration: ConfigurationContextValue = {
  companySectors: defaultCompanySectors,
  currency: defaultCurrency,
  dealCategories: defaultDealCategories,
  dealPipelineStatuses: defaultDealPipelineStatuses,
  dealStages: defaultDealStages,
  noteStatuses: defaultNoteStatuses,
  taskTypes: defaultTaskTypes,
  title: defaultTitle,
  darkModeLogo: defaultDarkModeLogo,
  lightModeLogo: defaultLightModeLogo,
};
